import { t } from "../translations";

/**
 * Translates backend error messages to the user's language
 * @param {string} backendMessage - The error message from the backend
 * @param {string} lang - The user's language preference ('en' or 'fr')
 * @param {boolean} isRestaurant - If true, uses restaurant variant of the key (default: false)
 * @returns {string} - The translated error message
 */
export const translateErrorMessage = (backendMessage, lang = "en", isRestaurant = false) => {
  if (!backendMessage || typeof backendMessage !== "string") {
    return backendMessage;
  }

  // Pattern 1: Minimum order amount error
  // "Order amount must be at least $10. Current order amount: $5.00"
  const minOrderPattern =
    /Order amount must be at least \$([\d.]+)\. Current order amount: \$([\d.]+)/;
  const minOrderMatch = backendMessage.match(minOrderPattern);
  if (minOrderMatch) {
    const minAmount = minOrderMatch[1];
    const currentAmount = minOrderMatch[2];
    const translation = t("errors.order_amount_too_low", lang);
    return translation
      .replace("${minAmount}", `$${minAmount}`)
      .replace("${currentAmount}", `$${currentAmount}`);
  }

  // Pattern 2: Delivery address too far error
  // "Delivery address is too far from the hub. Maximum delivery distance is 30 km, but the distance is 35.5 km. Please select a delivery address within the hub's coverage area."
  const deliveryDistancePattern =
    /Delivery address is too far from the hub\. Maximum delivery distance is ([\d.]+) km, but the distance is ([\d.]+) km\./;
  const deliveryDistanceMatch = backendMessage.match(deliveryDistancePattern);
  if (deliveryDistanceMatch) {
    const maxDistance = deliveryDistanceMatch[1];
    const distance = deliveryDistanceMatch[2];
    const translation = t("errors.delivery_address_too_far", lang);
    return translation
      .replace("${maxDistance}", maxDistance)
      .replace("${distance}", distance);
  }

  // Pattern 3: Fees exceed subtotal error
  // "Order cannot be processed: Total fees and charges ($16.58) exceed or equal the subtotal ($5.00). Total fees represent 331.60% of the subtotal. The maximum allowed is less than 100% to ensure the chef earns a positive amount."
  const feesExceedPattern =
    /Total fees and charges \(\$([\d.]+)\) exceed or equal the subtotal \(\$([\d.]+)\)\. Total fees represent ([\d.]+)% of the subtotal\./;
  const feesExceedMatch = backendMessage.match(feesExceedPattern);
  if (feesExceedMatch) {
    const totalFees = `$${feesExceedMatch[1]}`;
    const subtotal = `$${feesExceedMatch[2]}`;
    const percentage = feesExceedMatch[3];
    const translation = t("errors.fees_exceed_subtotal", lang);
    return translation
      .replace("${totalFees}", totalFees)
      .replace("${subtotal}", subtotal)
      .replace("${percentage}", percentage);
  }

  // Pattern 4: Chef/Restaurant earnings negative error
  // "Order cannot be processed: Chef would earn -$11.58. Chef earnings must be positive."
  // or "Order cannot be processed: Restaurant would earn -$11.58. Restaurant earnings must be positive."
  const chefEarningsPattern =
    /(?:Chef|Restaurant) would earn (-\$[\d.]+)\. (?:Chef|Restaurant) earnings must be positive\./;
  const chefEarningsMatch = backendMessage.match(chefEarningsPattern);
  if (chefEarningsMatch) {
    const earnedAmount = chefEarningsMatch[1];
    const translation = t("errors.chef_earnings_negative", lang, isRestaurant);
    return translation.replace("${earnedChef}", earnedAmount).replace("${earnedRestaurant}", earnedAmount);
  }

  // If no pattern matches, return the original message
  return backendMessage;
};
